import * as React from "react";
import { RBACButton } from "../../../molecules/rbac-button";
import OutsideClickHandler from "@graffer-inc/react-outside-click-handler";
import { Dropdown } from "../../../molecules/dropdown";
import { useAuthContextV2 } from "../../../../auth-state";
import { useModal } from "../../../../utils/custom-hooks";
import { toast } from "react-toastify";
import { SurveyEntity } from "@graffer-inc/smart-apply";
import { SecondaryButton } from "@graffer-inc/console-design-kit";

type MenuButtonsProps = {
  survey: SurveyEntity;
  orgSlug: string;
  onManageGroupOpen: () => void;
  onPublishSettingModalOpen: () => void;
  onUnPublish: () => void;
  onDelete: () => void;
  onPauseAccepting: () => void;
  onReturnToDraft: () => void;
  onResumeAccepting: () => void;
  onCopyOpen: () => void;
  onExportSurvey: () => void;
  onOpenPreview: (orgSlug: string) => void;
};

export const MenuButtons: React.FC<MenuButtonsProps> = ({
  survey,
  orgSlug,
  onManageGroupOpen,
  onPublishSettingModalOpen,
  onUnPublish,
  onDelete,
  onReturnToDraft,
  onResumeAccepting,
  onPauseAccepting,
  onCopyOpen,
  onExportSurvey,
  onOpenPreview,
}) => {
  const authState = useAuthContextV2();
  const menuToggle = useModal();

  const menus: Array<{
    title: string;
    onClick: () => void;
    disable?: boolean;
  }> = [];
  if (
    survey.publishStatus !== "DRAFT" &&
    authState.auth.checkGroupPermission(survey, ["surveys.update"])
  ) {
    menus.push({
      title: "公開・受付日時を変更する",
      onClick: onPublishSettingModalOpen,
    });
  }
  if (
    survey.publishStatus === "ACCEPTING" &&
    authState.auth.checkGroupPermission(survey, ["surveys.update"])
  ) {
    menus.push({
      title: "受付を一時停止する",
      onClick: onPauseAccepting,
    });
  }
  if (
    survey.isUnPublishable() &&
    authState.auth.checkGroupPermission(survey, ["surveys.unpublish"])
  ) {
    menus.push({
      title: "公開を終了する",
      onClick: onUnPublish,
    });
  }
  if (authState.auth.checkGroupPermission(survey, ["surveys.manage_groups"])) {
    menus.push({
      title:
        !!survey.groupIds && survey.groupIds.length > 0
          ? "管理グループを変更する"
          : "管理グループを設定する",
      onClick: onManageGroupOpen,
    });
  }
  if (authState.auth.checkGroupPermission(survey, ["surveys.create"])) {
    menus.push({
      title: "アンケートをコピーして新規作成する",
      onClick: onCopyOpen,
    });
  }
  if (
    !survey.isPublished() &&
    authState.auth.checkGroupPermission(survey, ["surveys.delete"])
  ) {
    menus.push({
      title: "アンケートを削除する",
      onClick: onDelete,
    });
  }
  if (authState.auth.checkGroupPermission(survey, ["surveys.create"])) {
    menus.push({
      title: "アンケートをエクスポートする",
      onClick: onExportSurvey,
    });
  }
  if (
    authState.auth.hasPermissions("graffer_utility_tools.generate_matrix_code")
  ) {
    const doorPageUrl = survey.answerSurveyURL(orgSlug);
    menus.push({
      title: "アンケートページの二次元コードを生成する",
      onClick: () =>
        window.open(
          `/graffer-utility-tools/matrix-code?queryUrl=${encodeURIComponent(
            doorPageUrl,
          )}`,
          "_blank",
        ),
    });
  }

  return (
    <div className="buttons has-text-right is-right">
      <SecondaryButton
        icon="OpenInNewIcon"
        text="アンケートプレビュー"
        onClick={() => onOpenPreview(orgSlug)}
        disabled={!survey.validatePublishable().publishable}
      />
      {survey.publishStatus === "BEFORE_PUBLISH" && (
        <RBACButton
          target={survey}
          requirePermissions={"surveys.update"}
          onClick={onReturnToDraft}
          buttonTypes={"secondary"}
          buttonText={"下書きに戻す"}
        />
      )}
      {survey.publishStatus === "PAUSING_ACCEPT" && (
        <RBACButton
          target={survey}
          requirePermissions={"surveys.update"}
          onClick={onResumeAccepting}
          buttonTypes={"primary"}
          buttonText={"受付を再開する"}
        />
      )}
      {survey.publishStatus === "DRAFT" && (
        <RBACButton
          target={survey}
          requirePermissions={"surveys.publish"}
          onClick={() => {
            const { publishable, errorMessage } = survey.validatePublishable();
            if (publishable) {
              onPublishSettingModalOpen();
              return;
            }

            toast.error(errorMessage);
          }}
          buttonTypes={"primary"}
          buttonText={"アンケートを公開する"}
        />
      )}
      {menus.length > 0 && (
        <OutsideClickHandler onOutsideClick={menuToggle.close}>
          <Dropdown
            onClick={menuToggle.open}
            isActive={menuToggle.isActive}
            isRight={true}
            menus={menus}
          />
        </OutsideClickHandler>
      )}
    </div>
  );
};
